import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiPlus, FiBox } from 'react-icons/fi';
import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import SearchInput from '../../components/ui/SearchInput';
import { Select } from '../../components/ui/Input';
import StatusPill from '../../components/ui/StatusPill';
import EmptyState from '../../components/ui/EmptyState';
import ErrorBanner from '../../components/ui/ErrorBanner';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';
import { useAuth } from '../../auth/AuthContext';

import {
  listAssets,
  listMyAssignedAssets,
  listAvailableAssets,
  listAssetsByStatus,
} from '../../api/assets';
import AssetForm from './AssetForm';
import AssetDetailModal from './AssetDetailModal';
import AssignAssetModal from './AssignAssetModal';

const ADMIN_TABS = [
  { key: 'all', label: 'All assets' },
  { key: 'available', label: 'Available' },
  { key: 'ASSIGNED', label: 'Assigned' },
  { key: 'MAINTENANCE', label: 'Maintenance' },
  { key: 'RETIRED', label: 'Retired' },
];

function useAssetsForTab(tab, isAdmin) {
  return useQuery({
    queryKey: isAdmin ? ['assets', 'tab', tab] : ['assets', 'mine'],
    queryFn: () => {
      if (!isAdmin) return listMyAssignedAssets();
      if (tab === 'all') return listAssets();
      if (tab === 'available') return listAvailableAssets();
      return listAssetsByStatus(tab);
    },
  });
}

export default function AssetsListPage() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);

  const { data, isLoading, error } = useAssetsForTab(tab, isAdmin);

  const filtered = useMemo(() => {
    const list = data || [];
    const term = search.trim().toLowerCase();
    return list.filter((a) => {
      if (categoryFilter && a.category !== categoryFilter) return false;
      if (!term) return true;
      return (
        (a.name && a.name.toLowerCase().includes(term)) ||
        (a.serialNumber && a.serialNumber.toLowerCase().includes(term)) ||
        (a.category && a.category.toLowerCase().includes(term))
      );
    });
  }, [data, search, categoryFilter]);

  const categories = useMemo(() => {
    const set = new Set();
    (data || []).forEach((a) => a.category && set.add(a.category));
    return Array.from(set).sort();
  }, [data]);

  const columns = [
    { key: 'name', header: 'Name', render: (r) => <span className="font-medium text-ink">{r.name}</span> },
    { key: 'category', header: 'Category' },
    {
      key: 'serialNumber',
      header: 'Serial',
      render: (r) => <span className="font-mono text-xs">{r.serialNumber}</span>,
    },
    {
      key: 'purchaseCost',
      header: 'Cost',
      align: 'right',
      render: (r) => <CurrencyDisplay value={r.purchaseCost} />,
    },
    {
      key: 'warrantyExpiry',
      header: 'Warranty',
      render: (r) => <DateDisplay value={r.warrantyExpiry} />,
    },
    {
      key: 'assignedTo',
      header: 'Assigned To',
      render: (r) =>
        r.assignedToUsername ? (
          <span className="text-xs text-ink-muted">{r.assignedToUsername}</span>
        ) : r.assignedTo ? (
          <span className="text-xs text-ink-muted">User #{r.assignedTo}</span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <StatusPill value={r.status} />,
    },
  ];

  return (
    <AppLayout title="Assets" breadcrumb="Finance / Assets">
      <PageHeader
        title={isAdmin ? 'Asset inventory' : 'My assigned assets'}
        description={
          isAdmin
            ? 'Register hardware, assign to employees, and track lifecycle.'
            : 'Hardware and equipment currently assigned to you.'
        }
        actions={
          isAdmin && (
            <Button icon={FiPlus} onClick={() => setShowForm(true)}>
              Register asset
            </Button>
          )
        }
      />

      {error && (
        <div className="mb-4">
          <ErrorBanner error={error} title="Failed to load assets" />
        </div>
      )}

      {isAdmin && (
        <div className="mb-4 flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-card">
          {ADMIN_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                tab === t.key
                  ? 'bg-primary text-white'
                  : 'text-ink-muted hover:bg-slate-100 hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name, serial, category..."
          className="w-full sm:max-w-sm"
        />
        {categories.length > 0 && (
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="sm:w-48"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        keyField="id"
        onRowClick={(row) => setSelected(row)}
        empty={
          <EmptyState
            icon={FiBox}
            title={isAdmin ? 'No assets registered' : 'No assets assigned to you'}
            description={
              isAdmin
                ? 'Register a new asset to begin tracking inventory.'
                : 'Reach out to admin to claim or request equipment.'
            }
            action={
              isAdmin && (
                <Button icon={FiPlus} onClick={() => setShowForm(true)}>
                  Register asset
                </Button>
              )
            }
          />
        }
      />

      {showForm && (
        <AssetForm open={showForm} onClose={() => setShowForm(false)} />
      )}

      <AssetDetailModal
        open={!!selected}
        asset={selected}
        onClose={() => setSelected(null)}
        onAssignClick={(asset) => {
          setSelected(null);
          setAssignTarget(asset);
        }}
      />

      <AssignAssetModal
        open={!!assignTarget}
        asset={assignTarget}
        onClose={() => setAssignTarget(null)}
      />
    </AppLayout>
  );
}
